import type { ReactNode } from 'react'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { setPreference, getPreference } from '@fs/zion-preferences'
import { useFeatureFlag } from '@fs/zion-flags'
import { localStorageGetItem, localStorageSetItem } from './utils/localStorage'

// Use user preference so that dark mode is set in all browsers used by user
// Use localStorage so that if user is in dark mode, they do not see things in light mode first while waiting for the user preference.

export type DarkModeValue = [boolean, boolean, (data?: boolean) => void]
export const preferenceKey = 'tree.person-page.dark-mode'

function useDarkModeHook(): DarkModeValue {
  const showDarkModeSetting = useFeatureFlag('treeweb_personR9_darkMode').isOn
  const cachedDarkModePref = localStorageGetItem(preferenceKey)
  const isDarkModeByCache = cachedDarkModePref === 'true'

  const [prefersDarkColorScheme, setPrefersDarkColorScheme] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )

  const [isForcedDarkMode, setIsForcedDarkMode] = useState<boolean | undefined>(
    cachedDarkModePref ? isDarkModeByCache : undefined
  )

  const updateDarkMode = useCallback((data?: boolean) => {
    setPreference(preferenceKey, data ?? '')
    localStorageSetItem(preferenceKey, data?.toString() ?? '')

    setIsForcedDarkMode(data)
  }, [])

  useEffect(() => {
    const updatePrefersColorScheme = (event): void => setPrefersDarkColorScheme(event.matches)
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', updatePrefersColorScheme)
    return () => {
      window.removeEventListener('change', updatePrefersColorScheme)
    }
  }, [])

  useEffect(() => {
    let isCurrent = true
    getPreference<boolean>(preferenceKey)
      .then((darkModePref) => {
        if (isCurrent && !!darkModePref !== isDarkModeByCache && darkModePref !== null) {
          localStorageSetItem(preferenceKey, darkModePref.toString())
          setIsForcedDarkMode(darkModePref === '' ? undefined : darkModePref)
        }
        return undefined
      })
      .catch(console.error)
    return () => {
      isCurrent = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only get the preference once at startup. After that we will just use the isDarkMode value.
  }, [])

  const isAutomaticMode = isForcedDarkMode === undefined

  return [showDarkModeSetting ? (isForcedDarkMode ?? prefersDarkColorScheme) : false, isAutomaticMode, updateDarkMode]
}

const DarkModeContext = createContext<DarkModeValue>([false, true, () => undefined])

export const useDarkMode = (): DarkModeValue => useContext(DarkModeContext)

type Props = {
  value?: DarkModeValue
  children: ReactNode
}
export default function DarkModeProvider({ value, children }: Props): JSX.Element {
  const darkModeValue = useDarkModeHook()
  return <DarkModeContext.Provider value={darkModeValue || value}>{children}</DarkModeContext.Provider>
}
